import { FlexibleContentFragment } from '@gql'
import React, { useMemo } from 'react'
import { getSection } from '../../../components/flexibleContent/fragments'
import { AltBg } from '../../../components/flexibleContent/fragments/altBg'
import { FlexibleContent as FlexibleContentBase } from '../../../components/flexibleContent/flexibleContent'
import { nonNull } from '../../../utils/nonNull'
import { iconObj } from '../../common/disclaimerIcons'
import {
  StyledPageDisclaimerBase,
  StyledPageDisclaimerContainer,
  StyledPageDisclaimerSymbol,
  StyledPageDisclaimerTitle,
  StyledPageDisclaimerWrapper,
} from '../styledPage'
import Grid from '@material-ui/core/Grid'
import { html } from '../../../utils/htmlParser'
import { LazyComponent } from '../../../components/lazyComponent/lazyComponent'
import { DisclaimerContext, DisclaimerIconMap, DisclaimerType } from '../../../context/disclaimerContext'
import { FlexibleContentRow } from '../../../components/flexibleContent'

export type FlexibleContentProps = {
  content: FlexibleContentFragment[]
  entryId?: string
}

type CarryType = (FlexibleContentFragment | null | FlexibleContentFragment[])[];

export const FlexibleContent: React.VFC<FlexibleContentProps> = ({ entryId, content }) => {
  const id = entryId

  const generalDisclaimerBlock = content?.filter(x => x.__typename === 'flexibleContent_disclaimer_BlockType')?.length

  const altBgContent = content.reduce((carry: CarryType, col) => {
    let block = col
    
    if (col.__typename === 'flexibleContent_altBgStart_BlockType') {
      //@ts-ignore
      block = [col]
    }

    let lastBlock = carry[carry.length - 1]

    if (col.__typename === 'flexibleContent_altBgEnd_BlockType') {
      if (!Array.isArray(lastBlock)) {
        lastBlock = []
        carry.push(lastBlock)
      }

      lastBlock.push(block)
      carry.push(null)

      return carry
    }

    if (Array.isArray(lastBlock)) {
      lastBlock.push(block)
    } else {
      carry.push(block)
    }

    return carry
  }, []).filter(Boolean)

  const disclaimerArray = useMemo(() => {
    const sortedArray: DisclaimerType[] = []

    content.map((content, _) => {
      // productTable blocks
      content?.__typename === 'flexibleContent_productTable_BlockType' && content?.table?.[0]?.__typename === 'rateTables_default_Entry' &&
      content?.table?.[0]?.rateTablePicker?.[0] && content?.table?.[0]?.rateTablePicker.map((rate, _) => {
        if ( rate?.__typename === 'rates_default_Entry') {
          if ( rate.homeLoanDisclaimers && rate.homeLoanDisclaimers?.length > 0 && rate?.homeLoanDisclaimers?.[0]?.__typename === 'homeLoanDisclaimers_default_Entry' ) {
            nonNull(rate?.homeLoanDisclaimers).map((item, _) => {
              {/** @ts-ignore */}

              if (item && item?.__typename === 'homeLoanDisclaimers_default_Entry') {
                {/** @ts-ignore */}

                if (sortedArray.some(sortedArray => sortedArray.disclaimerId === item?.disclaimerId)) {
                  return
                } else {
                  sortedArray.push(item as DisclaimerType)
                }
              }

              return null
            })
          }
          else if ( rate?.personalLoanDisclaimers && rate?.personalLoanDisclaimers?.length > 0 && rate?.personalLoanDisclaimers?.[0]?.__typename === 'personalloandisclaimers_default_Entry' ) {
            nonNull(rate.personalLoanDisclaimers).map((item, _) => {
              {/** @ts-ignore */}

              if (item && item?.__typename === 'personalloandisclaimers_default_Entry') {
                {/** @ts-ignore */}

                if (sortedArray.some(sortedArray => sortedArray.disclaimerId === item?.disclaimerId)) {
                  return
                } else {
                  sortedArray.push(item as DisclaimerType)
                }
              }

              return null
            })

            return null
          }

          return null
        }})

      // productComparisonTable blocks
      content?.__typename === 'flexibleContent_productComparisonTable_BlockType' &&
      content?.productComparisonTable &&
      content?.productComparisonTable.map((product, _) => {
        product?.__typename === 'productComparisonTable_BlockType' &&
        product?.productRate &&
        product?.productRate.map((rate, _) => {
          if (rate?.__typename === 'rates_default_Entry') {
            if (rate.homeLoanDisclaimers && rate.homeLoanDisclaimers?.length > 0 && rate?.homeLoanDisclaimers?.[0]?.__typename === 'homeLoanDisclaimers_default_Entry') {
              nonNull(rate?.homeLoanDisclaimers).map((item, _) => {
                {/** @ts-ignore */}

                if (item && item?.__typename === 'homeLoanDisclaimers_default_Entry') {
                  {/** @ts-ignore */}

                  if (sortedArray.some(existing => existing.disclaimerId === item?.disclaimerId)) {
                    return
                  } else {
                    sortedArray.push(item as DisclaimerType)
                  }
                }

                return null
              })
            }
            else if (rate?.personalLoanDisclaimers && rate?.personalLoanDisclaimers?.length > 0 && rate?.personalLoanDisclaimers?.[0]?.__typename === 'personalloandisclaimers_default_Entry') {
              nonNull(rate.personalLoanDisclaimers).map((item, _) => {
                {/** @ts-ignore */}

                if (item && item?.__typename === 'personalloandisclaimers_default_Entry') {
                  {/** @ts-ignore */}

                  if (sortedArray.some(existing => existing.disclaimerId === item?.disclaimerId)) {
                    return
                  } else {
                    sortedArray.push(item as DisclaimerType)
                  }
                }

                return null
              })

              return null
            }

            return null
          }
        })
      })
    })

    return sortedArray
  }, [content])

  const iconMap: DisclaimerIconMap = useMemo(() => {
    const iconMap = new Map()

    for (const [i, disclaimer] of disclaimerArray.entries()) {
      iconMap.set(disclaimer.disclaimerId, <>{iconObj[i] ? iconObj[i] : `~${i}`}</>)
    }

    return iconMap
  }, [disclaimerArray])

  const pageId = useMemo(() => {
    return id as string
  }, [id])

  return (
    <DisclaimerContext.Provider value={{ pageId, iconMap, disclaimerArray }}>
      <FlexibleContentBase>
        {altBgContent.map((props, i) => {
          if (Array.isArray(props)) {
            // This is an alt-bg section, which is an array bookended by an alt-bg start and end block

            // @ts-ignore
            const { colour, isGradient: $preGradient } = props[0]
            // @ts-ignore
            const { isGradient: $postGradient } = props[props.length - 1]

            const altBgSections = props.map((altBgProps, n) => {
              const Section = getSection(altBgProps)

              if (altBgProps.__typename === 'flexibleContent_altBgStart_BlockType') {
                return null
              }

              if (!Section) {
                console.warn(`Failed to locate section for ${altBgProps.__typename}`)
    
                return null
              }
    
              return (
                <LazyComponent key={n}>
                  {/** @ts-ignore */}
                  <Section { ...altBgProps } />
                </LazyComponent>
              )
            })

            return (
              <AltBg colour={colour} key={i} $postGradient={$postGradient} $preGradient={$preGradient}>
                {altBgSections}
              </AltBg>
            )
          }

          if (!props) {
            console.warn('Failed to locate section')

            return null
          }
          
          const Section = getSection(props)

          if (!Section) {
            console.warn(`Failed to locate section for ${props.__typename}`)

            return null
          }

          return (
            <LazyComponent key={i}>
              {/** @ts-ignore */}
              <Section { ...props } />
            </LazyComponent>
          )
        })}
        {(generalDisclaimerBlock === 0) && disclaimerArray && disclaimerArray.length > 0 &&
            <FlexibleContentRow>
              <StyledPageDisclaimerWrapper>
                <StyledPageDisclaimerTitle id={`disclaimer_block_${pageId}`} title>{'INFORMATION YOU SHOULD KNOW'}</StyledPageDisclaimerTitle>
                {disclaimerArray && nonNull(disclaimerArray).map((d, index) => {
                  return (
                    <StyledPageDisclaimerBase key={index} textBlock>
                      <StyledPageDisclaimerSymbol>
                        {iconMap.get(d.disclaimerId)}
                      </StyledPageDisclaimerSymbol>
                      {d?.disclaimerText && html(d.disclaimerText)}
                    </StyledPageDisclaimerBase>
                  )
                })}
              </StyledPageDisclaimerWrapper>
            </FlexibleContentRow>
        }
      </FlexibleContentBase>
    </DisclaimerContext.Provider>
  )
}
